import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ViewChild,
} from '@angular/core';
import { QuestionnaireBase } from '../../interfaces/questionnaire-base.interface';
import {
  HabitsConfiguration,
  HabitsQuestion,
} from 'src/app/analyses/interfaces/analyses.types';
import { CommonModule } from '@angular/common';
import { GpuFormFieldAppearance } from '@geneplanet/ngx-ui/src/lib/form-field';
import { SimpleOptionsComponent } from '../../answers/simple-options/simple-options.component';
import { QuestionnaireHostDirective } from '../../directives/questionnaire-host.directive';
import { AnswerBase } from '../../interfaces/answer-base.interface';
import { TranslateModule } from '@ngx-translate/core';
import { NumberInputComponent } from '../../answers/number-input/number-input.component';
import { InputOptionsComponent } from '../../answers/input-options/input-options.component';
import { NumberSliderComponent } from '../../answers/number-slider/number-slider.component';
import { TimeDropdownComponent } from '../../answers/time-dropdown/time-dropdown.component';
import { AnswerDict } from '../../interfaces/answer-dict.interface';
import { ExamplesModalButtonsComponent } from '../examples-modal-buttons/examples-modal-buttons.component';

/**
 * Simple questionnaire is designed to display one question only.
 */
@Component({
  selector: 'app-simple-questionnaire',
  templateUrl: './simple-questionnaire.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    QuestionnaireHostDirective,
    TranslateModule,
    ExamplesModalButtonsComponent,
  ],
  standalone: true,
})
export class SimpleQuestionnaireComponent implements QuestionnaireBase {
  @ViewChild(QuestionnaireHostDirective, { static: true })
  questionnaireHost!: QuestionnaireHostDirective;

  public questionnaire: HabitsConfiguration;
  public formFieldAppearance: GpuFormFieldAppearance;
  public answers: AnswerDict;
  public smallTitle: boolean;

  private componentRef: ComponentRef<AnswerBase>;

  protected get question(): HabitsQuestion {
    // Simple questionnaire only accepts one question
    return this.questionnaire.questions[0];
  }

  constructor(private cdRef: ChangeDetectorRef) {}

  public init() {
    this.loadComponent();
  }

  public onSubmit() {
    return this.componentRef.instance.onSubmit();
  }

  private loadComponent() {
    const component = this.selectComponent();

    const viewContainerRef = this.questionnaireHost.viewContainerRef;
    viewContainerRef.clear();

    this.componentRef = viewContainerRef.createComponent<AnswerBase>(component);
    this.componentRef.instance.question = this.question;
    this.componentRef.instance.formFieldAppearance = this.formFieldAppearance;
    this.componentRef.instance.answer = this.answers[this.question.id]?.[0];
    this.componentRef.instance.questionnaireId = this.questionnaire.id;

    // Init is optional
    if (this.componentRef.instance.init) {
      this.componentRef.instance.init();
    }

    this.cdRef.markForCheck();
  }

  private selectComponent() {
    enum Question {
      FRUITS = '5dcbc671-ee1f-4ac7-b0d2-6d5769d6395a',
      NUTS = 'e6d06d90-955c-403e-ac95-e3a16d13e3ca',
      GRAIN = 'f8bf8c39-21eb-4d83-9eab-39eb694bb478',
      MEAT = '559cbd1f-ad6d-452f-8094-7fe4b28ffc5b',
      SITTING = '217ce35c-164f-4e69-a612-70278eee419b',
      SMOKING = 'e652d53f-4a19-4ab2-a6da-3028862816ba',
      ALCOHOL = '667bf34d-0494-4fac-b06d-c7dbfdf3e3ae',
      BINGE_DRINKING = '7e90f5d1-18d0-41a0-986d-9c3e60ba727a',
      STRESS = '335aace7-c714-4049-b083-fe6b93fa3fe1',
      SLEEP = '1bb8d5e8-e149-43c3-a659-83f75c82ba62',
    }
    return (
      {
        [Question.FRUITS]: SimpleOptionsComponent,
        [Question.NUTS]: SimpleOptionsComponent,
        [Question.GRAIN]: SimpleOptionsComponent,
        [Question.MEAT]: SimpleOptionsComponent,
        [Question.SITTING]: NumberInputComponent,
        [Question.SMOKING]: InputOptionsComponent,
        [Question.ALCOHOL]: NumberInputComponent,
        [Question.BINGE_DRINKING]: SimpleOptionsComponent,
        [Question.STRESS]: NumberSliderComponent,
        [Question.SLEEP]: TimeDropdownComponent,
      }[this.questionnaire.id] ?? null
    );
  }
}
